import { useState } from 'react'

/**
 * DevModeBanner - sticky top-of-page strip rendered whenever a dev-mode
 * mock flag is active.
 *
 * Purpose: make it impossible for a developer to mistake mocked data /
 * mocked identity for a real environment. The banner is intentionally
 * loud (dark amber, white text, full width) and persists across all
 * routes - including sign-in / access-denied.
 *
 * Visibility logic is owned by the caller (in `app.tsx`); this component
 * just renders the strip. The caller passes the active flags so the text
 * accurately reflects what is mocked.
 */
export interface DevModeBannerProps {
  readonly mockAuth: boolean
  readonly mockBff: boolean
}

export function DevModeBanner({ mockAuth, mockBff }: DevModeBannerProps): JSX.Element | null {
  const [showTip, setShowTip] = useState(false)

  if (!mockAuth && !mockBff) return null

  // Compose a single human-readable summary of the active mocks. Order is
  // alphabetical so the string is stable across renders.
  const parts: string[] = []
  if (mockAuth) parts.push('auth: MOCK')
  if (mockBff) parts.push('BFF: MOCK')
  const summary = parts.join(' | ')

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Development mode banner"
      data-testid="dev-mode-banner"
      style={{
        // Inline styles so the banner renders identically even if Tailwind
        // has not loaded yet (e.g. very early render error).
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        width: '100%',
        backgroundColor: '#8a4b00',
        color: '#ffffff',
        padding: '6px 16px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: '13px',
        lineHeight: '1.4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        textAlign: 'center',
        boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
      }}
    >
      <strong style={{ letterSpacing: '0.04em' }}>DEV MODE</strong>
      <span aria-hidden="true">-</span>
      <span>{summary}</span>
      <button
        type="button"
        onClick={() => setShowTip((v) => !v)}
        onBlur={() => setShowTip(false)}
        aria-label="What is mocked?"
        aria-expanded={showTip}
        style={{
          background: 'transparent',
          border: '1px solid #ffffff',
          color: '#ffffff',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          fontSize: '12px',
          lineHeight: '18px',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        ?
      </button>
      {showTip && (
        <span
          role="tooltip"
          style={{
            marginLeft: '8px',
            padding: '2px 8px',
            backgroundColor: '#000000',
            color: '#ffffff',
            borderRadius: '3px',
            fontSize: '12px',
          }}
        >
          {tooltipText(mockAuth, mockBff)}
        </span>
      )}
    </div>
  )
}

function tooltipText(mockAuth: boolean, mockBff: boolean): string {
  const lines: string[] = []
  if (mockAuth) {
    lines.push(
      'Identity is a fake staff user (tnt=suitsme, roles=[bo.staff]); no Entra round-trip.',
    )
  }
  if (mockBff) {
    lines.push('BFF calls are intercepted by MSW with seed accounts; no network egress.')
  }
  return lines.join(' ')
}
